import { Components, Theme, createTheme } from '@mui/material'
import { border } from './border'
import { themeColor } from './color'
import { shadow } from './shadow'
import { surface } from './semanticTokens'

const material = createTheme()

export const muiOverrides: Components = {
  MuiTypography: {
    styleOverrides: {
      root: {
        ['&.MuiTypography-subtitle1']: {
          fontWeight: 600,
        },
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      fullWidth: true,
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        border: border.thin + ' solid ' + border.light,
        backgroundClip: 'padding-box',
        borderRadius: border.card.radius,
      },
      elevation1: {
        boxShadow: shadow.card,
      },
    },
  },
  // Alert and Snackbar are Papers too: no card border on them (outlined alerts keep their own).
  MuiAlert: {
    styleOverrides: {
      root: {
        ['&.MuiAlert-standard, &.MuiAlert-filled']: {
          border: 0,
        },
      },
    },
  },
  MuiSnackbarContent: {
    styleOverrides: {
      root: {
        border: 0,
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        ['& svg']: {
          marginRight: 10,
        },
      },
    },
  },
  // Underlined, sentence-case tabs (xxllnc.ai docs style).
  MuiTabs: {
    styleOverrides: {
      root: {
        minHeight: 44,
        marginBottom: material.spacing(2.5),
        borderBottom: border.thin + ' solid ' + border.light,
        ['& .MuiTabs-flexContainer']: {
          gap: material.spacing(3),
        },
      },
      indicator: {
        height: 2,
        borderRadius: border.radius.sm,
        backgroundColor: themeColor.blue,
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        minHeight: 44,
        minWidth: 0,
        padding: 0,
        textTransform: 'none',
        fontSize: 14,
        fontWeight: 500,
        color: themeColor.slate50,
        ['&:hover']: {
          color: themeColor.slate,
        },
        ['&.Mui-selected']: {
          color: themeColor.slate,
          fontWeight: 600,
        },
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      // One button look for MUI and react-admin buttons: 34px high, sentence case, 8px icon gap.
      root: {
        'color': themeColor.slate60,
        'height': 34,
        'padding': '0 14px',
        'borderRadius': border.button.radius,
        'minWidth': 'auto',
        'gap': 8,
        'fontSize': 13.5,
        'fontWeight': 600,
        'whiteSpace': 'nowrap' as const,
        'textTransform': 'none' as const,
        '& .MuiButton-startIcon > *:nth-of-type(1), & .MuiButton-endIcon > *:nth-of-type(1)': { fontSize: 18 },
      },
      contained: {
        backgroundColor: themeColor.blue,
        color: surface.white,
        boxShadow: 'none',
        ['&:hover']: {
          boxShadow: 'none',
        },
      },
      outlinedPrimary: {
        border: border.thin + ' solid ' + themeColor.slate20,
        ['&:hover']: {
          border: border.thin + ' solid ' + themeColor.slate40,
          backgroundColor: themeColor.slate05,
        },
      },
      // Icon spacing via gap: works for MUI (icon first) and react-admin
      // buttons (row-reverse, icon last) alike.
      startIcon: {
        margin: 0,
      },
      endIcon: {
        margin: 0,
      },
    },
  },
  MuiButtonBase: {
    styleOverrides: {
      root: ({ theme }: { theme?: unknown }) => ({
        ['&:hover:active::after']: {
          content: '""',
          display: 'block',
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: 0,
          right: 0,
          backgroundColor: 'currentColor',
          opacity: 0.3,
          borderRadius: 'inherit',
          // The press overlay must not catch the mouseup: it covers the children (a Chip's delete icon)
          // and the click would never reach them.
          pointerEvents: 'none',
        },
        // Keyboard focus only (:focus-visible): a visible ring, never after a mouse click.
        ['&.Mui-focusVisible']: {
          outline: `2px solid ${(theme as Theme).palette.primary.main}`,
          outlineOffset: 2,
        },
        ['&.MuiButton-contained']: {
          ['&:hover']: {
            backgroundColor: themeColor.blue80,
          },
        },
      }),
    },
    defaultProps: {
      disableRipple: true,
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        marginBottom: material.spacing(2),
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        borderRadius: '0 !important',
      },
      colorSecondary: {
        backgroundColor: surface.white,
        border: 0,
        borderBottom: border.thin + ' solid ' + border.light,
      },
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      colorPrimary: {
        backgroundColor: themeColor.slate10,
      },
      barColorPrimary: {
        backgroundColor: themeColor.blue,
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: border.input.radius,
      },
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: {
        display: 'none',
      },
    },
  },
  MuiGrid: {
    styleOverrides: {
      root: {
        ['& .ra-field']: {
          display: 'flex',
          padding: material.spacing(.5),
          ['& a']: {
            wordWrap: 'break-word',
          },
        },
      },
      container: {
        ['&.MuiGrid-spacing-xs-1']: {
          marginTop: material.spacing(2),
        },
        ['&.MuiGrid-spacing-xs-2']: {
          marginTop: material.spacing(2),
        },
        ['&.MuiGrid-spacing-xs-3']: {
          marginTop: material.spacing(2),
        }
      },
    },
  },
  MuiPopover: {
    styleOverrides: {
      paper: {
        background: surface.white,
        border: border.thin + ' solid ' + border.light,
        borderRadius: border.dropdown.radius,
        boxShadow: '0px 4px 12px rgba(15, 23, 42, 0.08), 0px 2px 4px rgba(15, 23, 42, 0.04)',
        ['& .MuiMenu-list']: {
          boxSizing: 'border-box',
          padding: material.spacing(1),
          ['& .MuiMenuItem-root']: {
            borderRadius: border.radius.sm,
            color: themeColor.slate80,
            marginBottom: 0,
            marginTop: 2,
            width: '100%',
            fontSize: 14,
            ['& .MuiSvgIcon-root']: {
              fill: themeColor.slate60,
            },
            ['&:first-of-type']: {
              marginTop: 0,
            },
            ['&:hover']: {
              color: themeColor.black,
              background: themeColor.slate05,
              ['& .MuiSvgIcon-root']: {
                fill: themeColor.slate80,
              },
            },
          },
          ['& .Mui-selected']: {
            background: themeColor.blue10,
            color: themeColor.blue,
            ['& .MuiSvgIcon-root']: {
              fill: themeColor.blue,
            },
          },
        },
      },
    },
  },
  MuiStepIcon: {
    styleOverrides: {
      root: {
        'color': themeColor.slate40,
        'fontWeight': 'bold',
        '&.Mui-active': {
          color: themeColor.blue,
        },
      },
    },
  },
  MuiStepLabel: {
    styleOverrides: {
      label: {
        '&.MuiStepLabel-alternativeLabel': {
          fontWeight: 400,
          fontSize: '8pt',
          color: themeColor.slate80,
        },
      },
    },
  },
  MuiListItemIcon: {
    styleOverrides: {
      root: {
        color: themeColor.slate60,
      },
    },
  },
}
